import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  MagnifyingGlass,
  PaperPlaneRight,
  Sparkle,
  X,
  ArrowClockwise,
  Lightning,
  Books,
  Circuitry,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXAMPLE_PROMPTS = [
  "Design a REST API for a multi-tenant SaaS with row-level security",
  "Optimize a slow Postgres query using EXPLAIN ANALYZE",
  "Build a scroll-driven 3D hero with React Three Fiber and GSAP",
  "Write a pytest suite with async fixtures and mocking",
];

function newSessionId() {
  return `sess_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`;
}

export default function SkillsAgent() {
  const [skills, setSkills] = useState([]);
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(() => newSessionId());
  const [activeSkill, setActiveSkill] = useState(null);
  const [skillDetail, setSkillDetail] = useState(null);
  const scrollRef = useRef(null);

  // Fetch skills catalog
  useEffect(() => {
    axios
      .get(`${API}/skills`)
      .then((res) => setSkills(res.data.skills || []))
      .catch((e) => console.error("Failed to load skills", e));
  }, []);

  // Autoscroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  const filteredSkills = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return skills;
    return skills.filter(
      (s) =>
        s.id.toLowerCase().includes(q) ||
        (s.description || "").toLowerCase().includes(q) ||
        (s.triggers || "").toLowerCase().includes(q) ||
        (s.domain || "").toLowerCase().includes(q)
    );
  }, [skills, query]);

  const openSkillDetail = async (skillId) => {
    setActiveSkill(skillId);
    setSkillDetail(null);
    try {
      const res = await axios.get(`${API}/skills/${skillId}`);
      setSkillDetail(res.data);
    } catch (e) {
      setSkillDetail({ error: "Failed to load skill" });
    }
  };

  const closeSkillDetail = () => {
    setActiveSkill(null);
    setSkillDetail(null);
  };

  const sendMessage = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || streaming) return;

    setInput("");
    const userMsg = { role: "user", content: text, skills_used: [], id: `u_${Date.now()}` };
    const asstMsg = {
      role: "assistant",
      content: "",
      skills_used: [],
      id: `a_${Date.now()}`,
      streaming: true,
    };
    setMessages((prev) => [...prev, userMsg, asstMsg]);
    setStreaming(true);

    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });

      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const jsonStr = line.slice(5).trim();
          if (!jsonStr) continue;
          try {
            const event = JSON.parse(jsonStr);
            if (event.type === "meta") {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === asstMsg.id
                    ? { ...m, skills_used: event.skills || [] }
                    : m.id === userMsg.id
                    ? { ...m, skills_used: event.skills || [] }
                    : m
                )
              );
              if (event.session_id) setSessionId(event.session_id);
            } else if (event.type === "delta") {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === asstMsg.id ? { ...m, content: m.content + event.content } : m
                )
              );
            } else if (event.type === "done") {
              setMessages((prev) =>
                prev.map((m) => (m.id === asstMsg.id ? { ...m, streaming: false } : m))
              );
            }
          } catch (e) {
            // ignore malformed chunk
          }
        }
      }
    } catch (err) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === asstMsg.id
            ? { ...m, content: `[error: ${err.message}]`, streaming: false }
            : m
        )
      );
    } finally {
      setStreaming(false);
      setMessages((prev) =>
        prev.map((m) => (m.role === "assistant" && m.streaming ? { ...m, streaming: false } : m))
      );
    }
  };

  const startNewSession = () => {
    setSessionId(newSessionId());
    setMessages([]);
  };

  return (
    <div className="skills-agent-theme h-screen flex bg-[var(--bg)] text-[var(--fg)] overflow-hidden" data-testid="app-root">
      {/* SIDEBAR: Skills Catalog */}
      <aside
        className="hidden md:flex flex-col border-r border-[var(--border)] bg-[var(--card)]"
        style={{ width: "340px", minWidth: "340px" }}
        data-testid="skills-sidebar"
      >
        <div className="border-b border-[var(--border)] p-5">
          <div className="flex items-center gap-2 mb-1">
            <Circuitry size={20} weight="duotone" className="text-[var(--accent)]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted-fg)]">
              CLAUDE / SKILLS
            </span>
          </div>
          <h1 className="font-display text-2xl font-black leading-tight">
            Skill Router<span className="text-[var(--accent)]">.</span>
          </h1>
          <p className="text-sm text-[var(--muted-fg)] mt-1">
            {skills.length} specialist skills. Ask anything — the router picks the best.
          </p>
        </div>

        <div className="p-4 border-b border-[var(--border)] sticky top-0 bg-[var(--card)]">
          <label className="relative block">
            <MagnifyingGlass
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-fg)]"
            />
            <input
              data-testid="skill-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search skills..."
              className="w-full pl-9 pr-3 py-2.5 text-sm border border-[var(--border)] bg-white font-mono focus-accent"
              style={{ borderRadius: 0 }}
            />
          </label>
          <div className="mt-2 flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-[var(--muted-fg)]">
            <span>{filteredSkills.length} MATCHING</span>
            <button
              data-testid="clear-search-btn"
              onClick={() => setQuery("")}
              className="hover:text-[var(--fg)] disabled:opacity-30"
              disabled={!query}
            >
              CLEAR
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scroll-thin" data-testid="skill-list">
          {filteredSkills.map((s) => (
            <button
              key={s.id}
              data-testid={`skill-list-item-${s.id}`}
              onClick={() => openSkillDetail(s.id)}
              className="w-full text-left px-4 py-3 border-b border-[var(--border)] hover:bg-[var(--muted)] transition-colors block group"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-mono text-sm font-semibold text-[var(--fg)] group-hover:text-[var(--accent)] transition-colors truncate">
                  {s.id}
                </div>
                {s.domain && (
                  <span className="shrink-0 font-mono text-[10px] uppercase tracking-widest text-[var(--muted-fg)] border border-[var(--border)] px-1.5 py-0.5">
                    {s.domain}
                  </span>
                )}
              </div>
              <p className="text-xs text-[var(--muted-fg)] mt-1 leading-relaxed line-clamp-2">
                {s.description}
              </p>
            </button>
          ))}
          {filteredSkills.length === 0 && (
            <div className="p-6 text-center text-sm text-[var(--muted-fg)]">No skills match.</div>
          )}
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 flex flex-col min-w-0" data-testid="main-chat">
        {/* Header */}
        <header className="flex items-center justify-between border-b border-[var(--border)] bg-[var(--card)] px-6 py-3">
          <div className="flex items-center gap-3">
            <Lightning size={18} weight="fill" className="text-[var(--accent)]" />
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted-fg)]">
                MODEL / claude-sonnet-4-6
              </div>
              <div className="font-display text-sm font-semibold">Auto-routing enabled</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-widest text-[var(--muted-fg)]">
              SESSION · {sessionId.slice(-6)}
            </span>
            <button
              data-testid="new-session-btn"
              onClick={startNewSession}
              className="flex items-center gap-1.5 border border-[var(--border)] px-3 py-1.5 text-xs font-mono uppercase tracking-widest hover:bg-[var(--fg)] hover:text-white transition-colors"
            >
              <ArrowClockwise size={14} />
              NEW
            </button>
          </div>
        </header>

        {/* Chat area */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto scroll-thin flex flex-col"
          data-testid="chat-scroll"
        >
          {messages.length === 0 ? (
            <div className="flex-1 flex flex-col">
              <EmptyState onExample={(t) => sendMessage(t)} skillCount={skills.length} />
            </div>
          ) : (
            <div className="max-w-4xl mx-auto py-8 px-6 md:px-10">
              {messages.map((m) => (
                <MessageBlock key={m.id} message={m} onSkillClick={openSkillDetail} />
              ))}
            </div>
          )}
        </div>

        {/* Input */}
        <div className="border-t border-[var(--border)] bg-[var(--card)] px-4 py-4 md:px-8 md:py-5">
          <form
            className="max-w-4xl mx-auto flex items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage();
            }}
          >
            <textarea
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Ask anything — the router will pick 1-3 skills automatically..."
              rows={1}
              disabled={streaming}
              className="flex-1 resize-none border border-[var(--border)] bg-white px-4 py-3 text-sm focus-accent disabled:opacity-60"
              style={{ borderRadius: 0, minHeight: "48px", maxHeight: "180px" }}
            />
            <button
              data-testid="send-button"
              type="submit"
              disabled={streaming || !input.trim()}
              className="h-[48px] px-5 flex items-center gap-2 bg-[var(--fg)] text-white font-mono text-xs uppercase tracking-widest disabled:opacity-40 hover:bg-[var(--accent)] transition-colors"
            >
              {streaming ? "..." : (
                <>
                  SEND
                  <PaperPlaneRight size={14} weight="fill" />
                </>
              )}
            </button>
          </form>
          <div className="max-w-4xl mx-auto mt-2 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-[var(--muted-fg)]">
            <span>ENTER TO SEND · SHIFT+ENTER FOR NEWLINE</span>
            <span>{skills.length} SKILLS LOADED</span>
          </div>
        </div>
      </main>

      {/* Skill detail sheet */}
      {activeSkill && (
        <SkillDetailSheet skill={skillDetail} skillId={activeSkill} onClose={closeSkillDetail} />
      )}
    </div>
  );
}

function EmptyState({ onExample, skillCount }) {
  return (
    <div className="relative min-h-full flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="relative max-w-2xl mx-auto text-center px-6 py-16">
        <div className="inline-flex items-center gap-2 border border-[var(--border)] px-3 py-1.5 mb-6 bg-white">
          <Sparkle size={14} weight="fill" className="text-[var(--accent)]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--muted-fg)]">
            {skillCount} SPECIALIST SKILLS · AUTO-ROUTED
          </span>
        </div>
        <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none mb-4">
          Ask something<br />
          <span className="text-[var(--accent)]">technical.</span>
        </h2>
        <p className="text-[var(--muted-fg)] text-base mb-10 max-w-lg mx-auto">
          Claude Sonnet 4.6 with dynamic skill injection. The router picks 1-3 experts from{" "}
          {skillCount} skills — Python, React, Postgres, Terraform, and more.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
          {EXAMPLE_PROMPTS.map((p, i) => (
            <button
              key={i}
              data-testid={`example-prompt-${i}`}
              onClick={() => onExample(p)}
              className="group border border-[var(--border)] bg-white p-4 hover:border-[var(--accent)] hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[var(--muted-fg)] group-hover:text-[var(--accent)] mb-1.5">
                <span>{`>`}</span>
                <span>EXAMPLE {i + 1}</span>
              </div>
              <div className="text-sm text-[var(--fg)] leading-snug">{p}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function MessageBlock({ message, onSkillClick }) {
  const isUser = message.role === "user";
  return (
    <div
      className="anim-fade-up py-6 border-b border-[var(--border)] last:border-b-0"
      data-testid={`message-${message.role}`}
    >
      <div className="flex items-center gap-2 mb-3">
        {isUser ? (
          <span className="w-6 h-6 bg-[var(--fg)] text-white grid place-items-center font-mono text-[11px] font-bold">
            U
          </span>
        ) : (
          <span className="w-6 h-6 bg-[var(--accent)] text-white grid place-items-center">
            <Sparkle size={12} weight="fill" />
          </span>
        )}
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--muted-fg)]">
          {isUser ? "USER" : "CLAUDE · SONNET 4.6"}
        </span>
      </div>

      {!isUser && message.skills_used && message.skills_used.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="router-badges">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-fg)] py-1">
            SKILLS LOADED →
          </span>
          {message.skills_used.map((s) => (
            <button
              key={s.id}
              data-testid={`router-badge-${s.id}`}
              onClick={() => onSkillClick(s.id)}
              className="anim-badge-pop inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest bg-[var(--accent-soft)] text-[var(--accent)] border border-[var(--accent)] px-2 py-1 hover:bg-[var(--accent)] hover:text-white transition-colors"
            >
              <Books size={11} weight="bold" />
              {s.id}
            </button>
          ))}
        </div>
      )}

      {isUser ? (
        <div className="text-[15px] leading-relaxed whitespace-pre-wrap text-[var(--fg)]">
          {message.content}
        </div>
      ) : (
        <div className={`md-body ${message.streaming ? "stream-cursor" : ""}`}>
          {message.content ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
          ) : (
            <span className="text-[var(--muted-fg)] text-sm">Thinking...</span>
          )}
        </div>
      )}
    </div>
  );
}

function SkillDetailSheet({ skill, skillId, onClose }) {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="skill-detail-sheet">
      <button
        aria-label="Close overlay"
        className="flex-1 bg-black/40"
        onClick={onClose}
        data-testid="skill-detail-overlay"
      />
      <div
        className="w-full max-w-2xl bg-white border-l border-[var(--border)] flex flex-col anim-fade-up"
        style={{ boxShadow: "-8px 0 32px rgba(0,0,0,0.08)" }}
      >
        <div className="flex items-center justify-between border-b border-[var(--border)] px-6 py-4 sticky top-0 bg-white z-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-fg)]">
              SKILL / DETAIL
            </div>
            <div className="font-mono text-lg font-semibold">{skillId}</div>
          </div>
          <button
            data-testid="close-skill-detail-btn"
            onClick={onClose}
            className="p-2 hover:bg-[var(--muted)] transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto scroll-thin px-6 py-6">
          {!skill ? (
            <div className="text-sm text-[var(--muted-fg)]">Loading...</div>
          ) : skill.error ? (
            <div className="text-sm text-red-600">{skill.error}</div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-6">
                {skill.domain && (
                  <Tag>{`DOMAIN: ${skill.domain}`}</Tag>
                )}
                {skill.role && <Tag>{`ROLE: ${skill.role}`}</Tag>}
              </div>
              {skill.triggers && (
                <div className="mb-6">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-fg)] mb-1">
                    TRIGGERS
                  </div>
                  <div className="text-sm text-[var(--fg)] font-mono leading-relaxed">
                    {skill.triggers}
                  </div>
                </div>
              )}
              <div className="mb-6 border-l-2 border-[var(--accent)] pl-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-fg)] mb-1">
                  DESCRIPTION
                </div>
                <div className="text-sm text-[var(--fg)] leading-relaxed">{skill.description}</div>
              </div>
              <div className="md-body" data-testid="skill-detail-body">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{skill.body || ""}</ReactMarkdown>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Tag({ children }) {
  return (
    <span className="font-mono text-[10px] uppercase tracking-widest border border-[var(--border)] bg-[var(--muted)] px-2 py-1">
      {children}
    </span>
  );
}
